/** Жалобы за период — ровно то, что отдаёт `GET /api/complaints/report`. */
import { apiFetch, query } from "@/lib/api";

export type AnswerState = "answered" | "unanswered" | "no_head" | "no_location";

export type Comment = {
  id: number;
  user_id: number;
  user: string | null;
  at: string;
  text: string | null;
  is_head: boolean;
};

export type Complaint = {
  id: number;
  date: string;
  location_id: number | null;
  location: string | null;
  location_name: string | null;
  category_id: number;
  category: string;
  external: boolean;
  status_id: number;
  status: string;
  in_stat: boolean;
  groups: string[];
  author_id: number;
  author: string | null;
  text: string | null;
  deal_id: number | null;
  expense: number;
  penalty: number;
  penalties: {
    user_id: number;
    name: string | null;
    location: string | null;
    amount: number;
  }[];
  answer: { state: AnswerState; by: string | null; at: string | null };
  heads: string[];
  comments: Comment[];
  waiting_days: number | null;
};

export type Salon = {
  location_id: number | null;
  short: string | null;
  name: string | null;
  heads: string[];
  count: number;
  external: number;
  unanswered: number;
  penalty: number;
  expense: number;
  prev_count: number;
  prev_penalty: number;
};

export type Count = {
  id: number | string | null;
  name: string | null;
  count: number;
};

export type ComplaintsReport = {
  period: { from: string; to: string };
  prev: { from: string; to: string };
  total: {
    count: number;
    external: number;
    unanswered: number;
    no_head: number;
    penalty: number;
    expense: number;
  };
  prev_total: { count: number; penalty: number };
  salons: Salon[];
  by_category: Count[];
  by_group: Count[];
  by_status: Count[];
  by_author: Count[];
  penalties: {
    user_id: number;
    name: string | null;
    location: string | null;
    amount: number;
    count: number;
  }[];
  items: Complaint[];
};

export function loadComplaints(
  from: string,
  to: string,
): Promise<ComplaintsReport> {
  return apiFetch<ComplaintsReport>(
    `/api/complaints/report${query({ from, to })}`,
  );
}

export const ANSWER_LABEL: Record<AnswerState, string> = {
  answered: "ответил руководитель",
  unanswered: "без ответа",
  no_head: "у салона нет руководителя",
  no_location: "без салона",
};

/** Жалоба в legacy-CRM (a.reserv), фильтр `helpTaskID` списка жалоб. */
export const crmComplaintUrl = (id: number) =>
  `https://a.unirenter.ru/b24/r.php?page=help&do=helpTaskList&helpTaskID=${id}`;

export const rub = (v: number) => `${v.toLocaleString("ru-RU")} ₽`;

const iso = (d: Date) => d.toLocaleDateString("sv-SE");

/** Готовые периоды; «прошлая неделя» — по умолчанию. */
export function presets(): {
  id: string;
  label: string;
  from: string;
  to: string;
}[] {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const lastMon = new Date(monday);
  lastMon.setDate(monday.getDate() - 7);
  const lastSun = new Date(monday);
  lastSun.setDate(monday.getDate() - 1);
  const m0 = new Date(now.getFullYear(), now.getMonth(), 1);
  const pm0 = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const pm1 = new Date(now.getFullYear(), now.getMonth(), 0);
  const q0 = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const y0 = new Date(now.getFullYear(), 0, 1);
  return [
    { id: "lw", label: "прошлая неделя", from: iso(lastMon), to: iso(lastSun) },
    { id: "w", label: "эта неделя", from: iso(monday), to: iso(now) },
    { id: "pm", label: "прошлый месяц", from: iso(pm0), to: iso(pm1) },
    { id: "m", label: "этот месяц", from: iso(m0), to: iso(now) },
    { id: "q", label: "квартал", from: iso(q0), to: iso(now) },
    { id: "y", label: "год", from: iso(y0), to: iso(now) },
  ];
}

/** Сдвиг периода на его длину: -1 — назад, +1 — вперёд. */
export function shiftPeriod(
  from: string,
  to: string,
  dir: -1 | 1,
): { from: string; to: string } {
  const a = new Date(`${from}T12:00:00`);
  const b = new Date(`${to}T12:00:00`);
  const days = Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
  a.setDate(a.getDate() + dir * days);
  b.setDate(b.getDate() + dir * days);
  return { from: iso(a), to: iso(b) };
}
